import { useState } from 'react';
import type { ProductoCatalogo } from '../../api/ops/plan';
import { Boton } from '../../componentes/Boton';
import { Casilla } from '../../componentes/Casilla';
import { Contador } from '../../componentes/Contador';
import { Desplegable } from '../../componentes/Desplegable';
import { Icono } from '../../componentes/Icono';
import { OpcionRadio } from '../../componentes/OpcionRadio';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Segmentos } from '../../componentes/Segmentos';
import { Ventana } from '../../componentes/Ventana';
import {
    COMPRAS,
    cambiarEleccion,
    nBloques,
    resumenDePago,
    textoCuota,
    type Catalogo,
    type Eleccion,
    type Elegible,
    type LineaFicha,
    type TipoCompra,
} from './bloques';
import { dinero } from './logica';
import type { PrecioDe } from './recarga';

/**
 * Piezas de «elegir lo que se contrata» (1.15 y 1.16): la ventana «Contratar el plan» con la elección de la prueba, «Añade a
 * tu plan», la ficha del bloque (RV Panel) y la ventana «Comprar más». Solo pintan: las llamadas las hace `Plan.tsx`, y solo
 * con `cobroDisponible()` (G1427).
 */

/** Filas clave · valor de una ventana (Base · N bloques · Total al mes; Cuota actual · Nueva cuota). */
export function Lineas({ lineas }: { lineas: readonly { clave: string; valor: string }[] }) {
    return (
        <div className="rv-plan__lineas">
            {lineas.map((l) => (
                <div key={l.clave} className="rv-plan__lineas-fila">
                    <span>{l.clave}</span>
                    <strong>{l.valor}</strong>
                </div>
            ))}
        </div>
    );
}

/**
 * «Contratar el plan»: la base va siempre; los bloques se marcan o se quitan antes de ir al pago («eliges al contratar»),
 * con lo que necesita cada uno. Debajo, Base · N bloques · Total al mes del catálogo (sin precios, sin cifras).
 */
export function VentanaContratar(props: {
    abierta: boolean;
    eleccion: Eleccion;
    onEleccion: (e: Eleccion) => void;
    elegibles: readonly Elegible[];
    catalogo: Catalogo;
    ocupado: boolean;
    movil: boolean;
    onCerrar: () => void;
    onPagar: () => void;
}) {
    const { eleccion, elegibles, catalogo } = props;
    const posibles = elegibles.map((e) => e.codigo);
    const r = resumenDePago(eleccion, catalogo);
    return (
        <Ventana
            abierta={props.abierta}
            titulo="Contratar el plan"
            texto="Pagas en la página segura de Stripe, con tarjeta o domiciliación SEPA, y vuelves aquí. Sin permanencia."
            accion={props.movil ? 'Ir al pago' : 'Ir al pago seguro'}
            accionIcono="lock"
            ocupado={props.ocupado}
            ocupadoTexto="Abriendo el pago…"
            movil={props.movil}
            onCerrar={props.onCerrar}
            onAccion={props.onPagar}
        >
            {props.abierta && elegibles.length > 0 && (
                <div className="rv-plan__eleccion" role="group" aria-label="Bloques que contratas">
                    <span className="rv-plan__sub">La base va siempre. Marca los bloques que quieres seguir usando.</span>
                    {elegibles.map((e) => {
                        const unidades = eleccion[e.codigo];
                        const cuota = textoCuota(e.cuota);
                        return (
                            <div key={e.codigo} className="rv-plan__eleccion-fila">
                                <Casilla
                                    marcada={unidades !== undefined}
                                    onCambio={(m) => {
                                        props.onEleccion(cambiarEleccion(eleccion, e.codigo, m, catalogo, posibles));
                                    }}
                                >
                                    <span className="rv-plan__eleccion-texto">
                                        <strong>{e.nombre}</strong>
                                        {cuota !== null && <span>{cuota}</span>}
                                    </span>
                                </Casilla>
                                {e.porUnidades && unidades !== undefined && (
                                    <Contador
                                        etiqueta={`Unidades de ${e.nombre}`}
                                        valor={unidades}
                                        min={1}
                                        max={99}
                                        onCambio={(n) => {
                                            props.onEleccion({ ...eleccion, [e.codigo]: n });
                                        }}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
            {props.abierta && r !== null && (
                <Lineas
                    lineas={[
                        { clave: 'Base', valor: dinero(r.base) },
                        ...(r.n === 0 ? [] : [{ clave: nBloques(r.n), valor: dinero(r.bloques) }]),
                        { clave: 'Total al mes', valor: dinero(r.total) },
                    ]}
                />
            )}
        </Ventana>
    );
}

/** Una tarjeta corta de «Añade a tu plan»: toda abre su ficha; «Añadir», secundario, abre la ventana de añadir. */
export function AnadeATuPlan(props: {
    productos: readonly ProductoCatalogo[];
    catalogo: Catalogo;
    puedeAnadir: boolean;
    movil: boolean;
    onFicha: (codigo: string) => void;
    onAnadir: (codigo: string) => void;
}) {
    if (props.productos.length === 0) {
        return null;
    }
    return (
        <section className="rv-plan__seccion">
            <div className="rv-plan__titulo">
                <h3>Añade a tu plan</h3>
                <span>Sin permanencia. Se suma a tu cuota desde hoy.</span>
            </div>
            <div className={`rv-plan__anade${props.movil ? ' rv-plan__anade--movil' : ''}`} role="list" aria-label="Añade a tu plan">
                {props.productos.map((p) => {
                    const cuota = p.price?.interval === 'month' ? p.price.unit : null;
                    return (
                        <div key={p.code} role="listitem" className="rv-plan__anade-tarjeta">
                            <button
                                type="button"
                                className="rv-plan__anade-ficha"
                                aria-label={`${p.name}: ver qué incluye`}
                                onClick={() => {
                                    props.onFicha(p.code);
                                }}
                            />
                            <span className="rv-plan__anade-cabeza">
                                <span className="rv-plan__baldosa rv-plan__baldosa--apagada">
                                    <Icono nombre="puzzle" />
                                </span>
                                <strong>{p.name}</strong>
                            </span>
                            <span className="rv-plan__anade-pie">
                                <span className="rv-plan__anade-precio">
                                    <strong>{cuota === null || cuota.amount === 0 ? 'Sin cuota' : dinero(cuota)}</strong>
                                    {cuota !== null && cuota.amount > 0 && <span>al mes</span>}
                                </span>
                                {props.puedeAnadir ? (
                                    <Boton
                                        tipo="secundario"
                                        icono="plus"
                                        aria-label={`Añadir ${p.name} a mi plan`}
                                        onClick={() => {
                                            props.onAnadir(p.code);
                                        }}
                                    >
                                        Añadir
                                    </Boton>
                                ) : (
                                    <Icono nombre="chevron-right" />
                                )}
                            </span>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

const PESTANAS = [
    { clave: 'incluye', etiqueta: 'Qué incluye', icono: 'layers' },
    { clave: 'requisitos', etiqueta: 'Requisitos', icono: 'link' },
    { clave: 'precio', etiqueta: 'Precio', icono: 'euro' },
] as const;

export type PestanaFicha = (typeof PESTANAS)[number]['clave'];

/** La ficha de un bloque (RV Panel, tamaño ficha) con Qué incluye · Requisitos · Precio y la acción del propietario. */
export function FichaBloque(props: {
    abierta: boolean;
    titulo: string;
    icono: 'layers' | 'puzzle';
    lineas: Readonly<Record<PestanaFicha, readonly LineaFicha[]>>;
    nota: string | null;
    accion: string;
    accionIcono: 'plus' | 'minus' | 'hash';
    onAccion: () => void;
    /** Un bloque por unidades también se da de baja (botón secundario dentro de la ficha). */
    onQuitar: (() => void) | null;
    pideAlPropietario: boolean;
    movil: boolean;
    onCerrar: () => void;
}) {
    const [pestana, setPestana] = useState<PestanaFicha>('incluye');
    const i = PESTANAS.findIndex((p) => p.clave === pestana);
    const lineas = props.lineas[pestana];
    return (
        <PanelLateral
            abierto={props.abierta}
            onCerrar={props.onCerrar}
            movil={props.movil}
            tamano="ficha"
            titulo={props.titulo}
            icono={props.icono}
            accion={props.accion}
            accionIcono={props.accionIcono}
            onAccion={props.onAccion}
        >
            <div className="rv-plan__ficha">
                <Segmentos
                    etiqueta="Apartados del bloque"
                    movil={props.movil}
                    valor={i}
                    items={PESTANAS.map((p) => ({ etiqueta: p.etiqueta, icono: p.icono }))}
                    onCambio={(n) => {
                        setPestana(PESTANAS[n]?.clave ?? 'incluye');
                    }}
                />
                <div className="rv-plan__ficha-ceja">{PESTANAS[i]?.etiqueta}</div>
                <div className="rv-plan__ficha-lineas">
                    {lineas.length === 0 ? (
                        <span className="rv-plan__sub">El catálogo aún no detalla qué incluye este bloque.</span>
                    ) : (
                        lineas.map((l) => (
                            <div key={l.texto} className="rv-plan__ficha-linea">
                                <span>
                                    {l.icono !== undefined && <Icono nombre={l.icono} className={l.rojo === true ? 'rv-plan__rojo' : undefined} />}
                                    {l.texto}
                                </span>
                                {l.valor !== undefined && <strong>{l.valor}</strong>}
                            </div>
                        ))
                    )}
                </div>
                {pestana === 'precio' && props.nota !== null && <span className="rv-plan__sub">{props.nota}</span>}
                {props.onQuitar !== null && (
                    <Boton tipo="secundario" icono="minus" movil={props.movil} onClick={props.onQuitar}>
                        Dar de baja el bloque
                    </Boton>
                )}
                {props.pideAlPropietario && (
                    <p className="rv-plan__aviso">
                        <Icono nombre="lock" />
                        <span>Pídeselo al propietario: solo él puede añadir bloques al plan.</span>
                    </p>
                )}
            </div>
        </PanelLateral>
    );
}

/** Cambiar las unidades de un bloque por cantidad (profesionales extra): el Contador y la cuota que queda. */
export function VentanaUnidades(props: {
    abierta: boolean;
    nombre: string;
    actuales: number;
    unidades: number;
    onUnidades: (n: number) => void;
    lineas: readonly { clave: string; valor: string }[];
    ocupado: boolean;
    movil: boolean;
    onCerrar: () => void;
    onGuardar: () => void;
}) {
    return (
        <Ventana
            abierta={props.abierta}
            titulo={`Unidades de ${props.nombre}`}
            texto={`Tienes ${String(props.actuales)}. El cambio se aplica a tu cuota desde hoy, con prorrateo. No puedes bajar de las que estás usando.`}
            accion="Guardar"
            accionIcono="check"
            inactiva={props.unidades === props.actuales}
            ocupado={props.ocupado}
            movil={props.movil}
            onCerrar={props.onCerrar}
            onAccion={props.onGuardar}
        >
            {props.abierta && <Contador etiqueta="Unidades" valor={props.unidades} min={1} max={99} onCambio={props.onUnidades} />}
            {props.abierta && props.lineas.length > 0 && <Lineas lineas={props.lineas} />}
        </Ventana>
    );
}

/** «Comprar más» (1.16): qué quieres ampliar, el tamaño con su precio y lo que se cobra hoy. */
export function VentanaComprarMas(props: {
    abierta: boolean;
    tipos: readonly TipoCompra[];
    tipo: TipoCompra;
    onTipo: (t: TipoCompra) => void;
    tamano: number;
    onTamano: (n: number) => void;
    catalogo: Catalogo;
    precioDe: PrecioDe;
    finDelMes: string | null;
    metodo: string | null;
    ocupado: boolean;
    movil: boolean;
    onCerrar: () => void;
    onPagar: () => void;
}) {
    const d = COMPRAS[props.tipo];
    const tamanos = d.tamanos.map((t) => ({ ...t, texto: t.etiqueta(props.catalogo?.find((p) => p.code === t.codigo) ?? null), precio: props.precioDe(t.codigo) }));
    const elegido = tamanos[Math.min(props.tamano, tamanos.length - 1)];
    const precio = elegido?.precio ?? null;
    return (
        <Ventana
            abierta={props.abierta}
            titulo="Comprar más"
            texto="Elige qué quieres ampliar. Se cobra al momento."
            accion={precio === null ? 'Pagar' : `Pagar · ${precio}`}
            accionIcono="shopping-cart"
            inactiva={precio === null}
            ocupado={props.ocupado}
            ocupadoTexto="Pagando…"
            movil={props.movil}
            onCerrar={props.onCerrar}
            onAccion={props.onPagar}
        >
            {props.abierta && (
                <>
                    <Desplegable
                        etiqueta="Qué quieres ampliar"
                        valor={props.tipo}
                        opciones={props.tipos.map((t) => ({ valor: t, texto: COMPRAS[t].nombre }))}
                        onChange={(v) => {
                            props.onTipo(v as TipoCompra);
                        }}
                    />
                    <span className="rv-plan__sub rv-plan__compra-texto">{d.texto(props.finDelMes)}</span>
                    <div className="rv-plan__tamanos" role="radiogroup" aria-label="Tamaño">
                        {tamanos.map((t, n) => (
                            <OpcionRadio
                                key={t.codigo}
                                elegida={n === props.tamano}
                                onElegir={() => {
                                    props.onTamano(n);
                                }}
                                titulo={
                                    <span className="rv-plan__tamano">
                                        <span>{t.texto}</span>
                                        {t.precio !== null && <span>{t.precio}</span>}
                                    </span>
                                }
                            />
                        ))}
                    </div>
                    <div className="rv-plan__cobro-hoy">
                        <span>
                            <span>Se cobra hoy</span>
                            <span className="rv-plan__sub">{props.metodo ?? 'Con tu método de pago guardado'}</span>
                        </span>
                        <strong>{precio ?? '—'}</strong>
                    </div>
                </>
            )}
        </Ventana>
    );
}
